import type { Scene } from '@babylonjs/core';

/**
 * Loads each key's value once and shares it (#324). Concurrent callers
 * await the same load, and a failed load is dropped so the next caller
 * tries again (one transient failure must not poison a model for good).
 * `clear()` disposes what it holds; a load still running then is never
 * kept or handed out: it is disposed when it lands and its callers are
 * rejected, so nothing made for a disposed scene reaches a later one.
 */
export class LoadOnceCache<K, V> {
    private readonly loads = new Map<K, Promise<V>>();
    private readonly values = new Map<K, V>();
    /** Bumped by `clear()`, so a load started before it knows it is stale. */
    private generation = 0;

    constructor(private readonly disposeValue: (value: V) => void = () => undefined) {}

    /** The key's value, from `load` the first time it is asked for. */
    get(key: K, load: () => Promise<V>): Promise<V> {
        const existing = this.loads.get(key);
        if (existing) return existing;
        const generation = this.generation;
        const loading = load().then(
            (value) => {
                if (generation !== this.generation) {
                    this.disposeValue(value);
                    throw new Error(`Load of ${String(key)} landed after its cache was cleared`);
                }
                this.values.set(key, value);
                return value;
            },
            (error: unknown) => {
                if (this.loads.get(key) === loading) this.loads.delete(key);
                throw error;
            },
        );
        this.loads.set(key, loading);
        return loading;
    }

    /** Disposes every value it holds and forgets every load. */
    clear(): void {
        this.generation += 1;
        this.values.forEach((value) => this.disposeValue(value));
        this.values.clear();
        this.loads.clear();
    }
}

/**
 * A `LoadOnceCache` per scene, cleared with its scene: values that are
 * scene objects (meshes, containers) never outlive it or reach another.
 */
export class SceneLoadOnceCaches<K, V> {
    private readonly caches = new WeakMap<Scene, LoadOnceCache<K, V>>();

    constructor(private readonly disposeValue: (value: V) => void = () => undefined) {}

    /** The scene's cache, made the first time anything asks. */
    of(scene: Scene): LoadOnceCache<K, V> {
        let cache = this.caches.get(scene);
        if (!cache) {
            const created = new LoadOnceCache<K, V>(this.disposeValue);
            cache = created;
            this.caches.set(scene, created);
            scene.onDisposeObservable.addOnce(() => {
                this.caches.delete(scene);
                created.clear();
            });
        }
        return cache;
    }
}
